<script setup lang="ts">
import cloneDeep from 'lodash/cloneDeep'
import OverlayPanel from 'primevue/overlaypanel'
import { OnClickOutside } from '@vueuse/components'
import { IOpenChoice, IOpenCustomization } from '~/lib/models/events'
import {
  OptionsGroupsChild,
  OptionsGroupsMain,
  productInfoKey,
} from '~/lib/services/store/catalog/catalog.dtos'
import { OptionsGroupsType } from '~/lib/services/store/common.dto'

const props = defineProps<{
  optionsGroups: OptionsGroupsMain[]
  price: string
}>()

const { t } = useLocale()
const { trackCustomizeItem, trackSaveIngredients } = useGTM()
const emit = defineEmits(['save-personalization', 'customizing'])

const isMobile = inject<Ref<boolean>>('isMobile')
const productInfo = inject(productInfoKey)!

// When user selects a row contain OptGroups inside for customize
const customizingOptionGroups = ref<OptionsGroupsMain[] | OptionsGroupsChild[]>()
// Options to choose from
const choiceOptionGroup = ref<OptionsGroupsMain | OptionsGroupsChild>()
// while customizing a choice with several 3º level groups store that groups
const choiceOptionGroupChildGroups = ref<OptionsGroupsChild[]>()
// current third level group customizing on choice
const choiceOptionGroupChild = ref<OptionsGroupsChild>()
// initial state of optionGroups in case user closes to reset personalization
const initialStateCustomizinOptionGroups = ref<OptionsGroupsMain[]>()
// same for combos that have more optionsGroups inside, upper variable gets changed when open closes inside optionGroups
// const initialStateComboCustomizinOptionGroups = ref<OptionsGroupsMain[]>()
const op = ref<OverlayPanel>()
// condiment
const customizingCondimentGroup = ref<boolean>(false)
// analytics
const openedComponent = ref()

const mainCustomizingTitle = computed(() => {
  if (customizingOptionGroups.value) return t('productDetail.customization.customizingTitle')
  return choiceOptionGroupChild.value?.title || choiceOptionGroup.value?.title || ''
})

/* Customizing optionsGroups */
const openCustomization = (
  ev: PointerEvent,
  optsGroups: OptionsGroupsMain[] | OptionsGroupsChild[],
  openedAnalyticsComponent: { id: string; name: string },
  isCondimentGroup = false
) => {
  trackCustomizeItem(productInfo)
  // reset other state
  choiceOptionGroup.value = undefined
  initialStateCustomizinOptionGroups.value = cloneDeep(props.optionsGroups)

  openedComponent.value = openedAnalyticsComponent
  customizingCondimentGroup.value = isCondimentGroup
  // Not show 3º level optionsGroups on customization (CHOICE,SUGGESTIVE AND COMMENT)  still CONDIMENT could be here inside
  const excludedOptionsGroupsTypes = [
    OptionsGroupsType.CHOICE,
    OptionsGroupsType.SUGGESTIVE,
    OptionsGroupsType.COMMENT,
  ] as string[]
  customizingOptionGroups.value = optsGroups.filter(
    optG => !excludedOptionsGroupsTypes.includes(optG.type as string)
  )

  // on dekstop take parent (optionGroup row) from clicked button to show OverlayPanel on there
  if (!isMobile?.value) {
    // a lot of magic here
    const rowClickedRef = (ev.target as any)?.parentElement
    op.value?.toggle(ev, rowClickedRef)
  }
  emit('customizing', true)
}

const closeCustomizationOptGroup = ({ trackSave }: { trackSave: boolean }) => {
  if (trackSave) {
    const ingredientsStr = customizingOptionGroups
      .value!.map(optGMain => {
        const optionsIngredients = []
        for (const optM of optGMain.options) {
          if (optM.initialQty !== optM.qty) {
            optionsIngredients.push(`${optM.name}_${optM.qty}`)
          }
        }
        return optionsIngredients.length ? optionsIngredients.join(';') : null
      })
      .filter(v => v)
      .join(';')
    trackSaveIngredients(productInfo, openedComponent.value, ingredientsStr)
  }
  op.value?.hide()
  customizingOptionGroups.value = undefined
  openedComponent.value = undefined
  customizingCondimentGroup.value = false
  initialStateCustomizinOptionGroups.value = undefined
  emit('customizing', false)
}

const closeCustomizationOptGroupAndReset = () => {
  emit('save-personalization', initialStateCustomizinOptionGroups.value)
  closeCustomizationOptGroup({ trackSave: false })
}

/* Choosing option inside optionGroups */
const openChoice = (ev: PointerEvent, optGroup: OptionsGroupsMain) => {
  trackCustomizeItem(productInfo)
  // reset other state
  customizingOptionGroups.value = undefined
  emit('customizing', true)

  initialStateCustomizinOptionGroups.value = cloneDeep(props.optionsGroups)
  choiceOptionGroup.value = optGroup

  // on dekstop take parent (optionGroup row) from clicked button to show OverlayPanel on there
  if (!isMobile?.value) {
    // a lot of magic here
    const rowClickedRef = (ev.target as any)?.parentElement
    op.value?.toggle(ev, rowClickedRef)
  }
}

const closeChoice = async ({ closeModal = false }) => {
  // close clicked from modal (mobile), close all steps and hide ModalCenter
  if (closeModal) {
    emit('save-personalization', initialStateCustomizinOptionGroups.value)
    choiceOptionGroupChildGroups.value = undefined
    choiceOptionGroupChild.value = undefined
  }
  // IS open choice MAIN now
  else if (!choiceOptionGroupChild.value) {
    // Select to SHOW choice CHILD
    // Current optionGroup choosing is CHOICE
    if (choiceOptionGroup.value?.type === OptionsGroupsType.CHOICE) {
      // Get 1º level option selected
      const optionSelected = choiceOptionGroup.value.options.find(opt => opt.qty === 1)

      // Filter has 3º level optionsGroups is CHOICE
      const choiceOptionGroup3levelGroups = optionSelected?.optionsGroups.filter(
        ({ type }) => type === OptionsGroupsType.CHOICE
      )
      // Is 3º level optionsGroups CHOICE and must select option to continue
      if (choiceOptionGroup3levelGroups?.length) {
        choiceOptionGroupChildGroups.value = choiceOptionGroup3levelGroups
        choiceOptionGroupChild.value = choiceOptionGroup3levelGroups[0]
        return
      }
    }
  } else {
    // has child choice OPENED, will check first if has more 3º level groups to show
    // Find current 3º level CHOICE optionsGroups index
    const currentChoiceOptionGroup3levelIdx =
      choiceOptionGroupChildGroups.value?.findIndex(
        ({ identifier }) => identifier === choiceOptionGroupChild.value?.identifier
      ) ?? -1

    if (currentChoiceOptionGroup3levelIdx !== -1) {
      const nextChoiceOptionGroup3Level =
        choiceOptionGroupChildGroups.value?.[currentChoiceOptionGroup3levelIdx + 1]

      // if next group is CHOICE too, show it
      if (nextChoiceOptionGroup3Level?.type === OptionsGroupsType.CHOICE) {
        choiceOptionGroupChild.value = nextChoiceOptionGroup3Level
        return
      }
    }
    // no more groups to show, clean and let continue function
    choiceOptionGroupChild.value = undefined
    choiceOptionGroupChildGroups.value = undefined
  }
  op.value?.hide()
  emit('customizing', false)

  choiceOptionGroup.value = undefined
  initialStateCustomizinOptionGroups.value = undefined
}

const closeAndReset = (closeFromModal = false) => {
  if (customizingOptionGroups.value) closeCustomizationOptGroupAndReset()
  else if (choiceOptionGroup.value || closeFromModal) closeChoice({ closeModal: true })
}
</script>

<template>
  <section class="my-4">
    <h2 class="text-lg font-bold md:text-3xl">
      {{ t('productDetail.customization.title') }}
    </h2>

    <!-- *** Customizing several optionGroup *** -->

    <!-- OverlayPanel when customizing optionGroups is opened -->
    <OverlayPanel
      v-if="!isMobile"
      ref="op"
      :dismissable="false"
      :pt="{
        content: { class: '!p-0 speedee-font-family' },
      }"
    >
      <OnClickOutside @trigger="closeChoice({ closeModal: true })">
        <div class="shadow-elevation-up md:min-w-[25rem] md:max-w-lg">
          <div class="flex flex-row items-center gap-4 p-4">
            <CloseIcon
              width="18"
              height="18"
              class="cursor-pointer"
              @click="closeChoice({ closeModal: true })"
            />

            <h3 class="text-2xl font-bold">
              {{ mainCustomizingTitle }}
            </h3>
          </div>
          <LazyCustomizationOptionsGroups
            :options-groups="customizingOptionGroups"
            :choice-group="choiceOptionGroupChild || choiceOptionGroup"
            @close-choice="closeChoice({ closeModal: false })"
          />
          <!-- price, Actions -->
          <div
            v-if="!choiceOptionGroup"
            class="flex flex-row items-center justify-between bg-mcd-secondary p-4 px-10"
            :class="{ '!justify-end': customizingCondimentGroup }"
          >
            <p v-if="!customizingCondimentGroup" class="text-lg font-bold md:text-xl">
              {{ price }}
            </p>
            <McdButton
              is-primary
              class="px-[18px] py-3"
              @on-click="closeCustomizationOptGroup({ trackSave: true })"
            >
              {{ t('productDetail.customization.actions.save') }}
            </McdButton>
          </div>
        </div>
      </OnClickOutside>
    </OverlayPanel>

    <!-- ModalCenter (bottom) when customizing optionGroups is opened -->
    <template v-if="isMobile && (customizingOptionGroups?.length || choiceOptionGroup)">
      <LazyModalCenter :add-close-button="false" :mobile-full="false" no-actions-class>
        <template #header>
          <div class="flex flex-row items-center gap-2 py-4 pb-0">
            <CloseIcon
              width="12"
              height="12"
              class="mx-3 cursor-pointer"
              @click="closeAndReset(true)"
            />
            <h3 class="text-lg font-bold">
              {{ mainCustomizingTitle }}
            </h3>
          </div>
        </template>
        <template #content>
          <CustomizationOptionsGroups
            :options-groups="customizingOptionGroups"
            :choice-group="choiceOptionGroupChild || choiceOptionGroup"
            @close-choice="closeChoice({ closeModal: false })"
          />
        </template>
        <template #actions>
          <div
            v-if="!choiceOptionGroup"
            class="flex h-[64px] w-full flex-row items-center justify-between p-4 shadow-elevation-up"
            :class="{ '!p-0': customizingCondimentGroup }"
          >
            <p v-if="!customizingCondimentGroup" class="text-lg font-bold md:text-xl">
              {{ price }}
            </p>
            <McdButton
              is-primary
              class="px-4 py-3"
              :class="{ 'h-full w-full': customizingCondimentGroup }"
              @on-click="closeCustomizationOptGroup({ trackSave: true })"
            >
              {{ t('productDetail.customization.actions.save') }}
            </McdButton>
          </div>
        </template>
      </LazyModalCenter>
    </template>

    <!-- *** Customization first view, rows of optionsGroups *** -->
    <div v-if="optionsGroups.length" class="my-4">
      <template v-for="optGM in optionsGroups" :key="optGM.identifier">
        <template v-if="optGM.type === OptionsGroupsType.CHOICE">
          <ChoiceRow
            :option-group="optGM"
            @open-customization="(payload: IOpenCustomization) => openCustomization(payload.event, payload.optionsGroups, payload.openedComponent)"
            @open-choice="(payload: IOpenChoice) => openChoice(payload.event, payload.optionGroup)"
          ></ChoiceRow>
        </template>

        <!-- On other type than CHOICE not show SUGGESTIVE and COMMENT, and CONDIMENT (as regular CustomizationRowCombo) -->
        <CustomizationRowCombo
          v-else-if="
            optGM.type !== OptionsGroupsType.SUGGESTIVE &&
            optGM.type !== OptionsGroupsType.COMMENT &&
            optGM.type !== OptionsGroupsType.CONDIMENT
          "
          :option-group="optGM"
          @open-customization="(payload: IOpenCustomization) => openCustomization(payload.event, payload.optionsGroups, payload.openedComponent)"
        />

        <CustomizationRowComboCondiment
          v-else-if="optGM.type === OptionsGroupsType.CONDIMENT"
          :option-group="optGM"
          @open-customization="(payload: IOpenCustomization) => openCustomization(payload.event, payload.optionsGroups, payload.openedComponent, true)"
        />
      </template>
    </div>
  </section>
</template>
